import type { LandSystem, IntakeAdvisory, OPTIONAL_NOTIFICATION_CHANNELS } from "@titleverify/shared";

/**
 * CLIENT-FACING COPY THAT MORE THAN ONE SCREEN NEEDS.
 * ===========================================================================
 *
 * These three tables started life inside ClientCases.tsx, where the intake form
 * is the only thing that used them. They are lifted here because the supplement
 * form and the consent-withdrawal control need the same words, and the
 * alternative — copy-pasting them into a component — produces two tables that
 * say slightly different things about the same act. On this product that is not
 * a tidiness problem: CHANNEL_CONSENT is the statement of a cross-border
 * transfer, and a second divergent copy of it is a second, unreviewed
 * disclosure.
 *
 * A MODULE AND NOT A RE-EXPORT FROM THE PAGE. ClientCases renders the
 * components that need these; a component importing back from the page that
 * renders it closes an import cycle (the same reason `useMe` was pulled out of
 * App.tsx).
 *
 * WHAT DOES *NOT* BELONG HERE. Anything an attorney would want to rule on lives
 * in packages/shared — GAP_GUIDANCE, the vocabularies, the sufficiency rule.
 * This file is presentation for things the shared package sends as bare codes,
 * plus the plain-language account of what a notification channel means. Nothing
 * here states a fact about anybody's land.
 */

/**
 * Advisories arrive as bare codes — the server sends no copy for them, because
 * unlike a gap they ask nothing of the client that would block the case.
 *
 * Typed against IntakeAdvisory so a new advisory in the shared package fails to
 * compile here rather than rendering to a client as `EXTENT_MISSING`. Each line
 * asks for something; none states anything about the land.
 */
export const ADVISORY_PROMPT: Record<IntakeAdvisory, string> = {
  EXTENT_MISSING: "The extent — how large the land is — if it appears on your papers.",
  DS_DIVISION_MISSING: "The Divisional Secretariat division the land falls in.",
  VILLAGE_MISSING: "The village or town it is in.",
  NO_PRIOR_DEEDS:
    "Any earlier deeds you have references for. The chain has to be traced back at least thirty years, and a starting point you already hold saves looking for it.",
  LAND_SYSTEM_UNKNOWN:
    "Which registration system the land is under, if you happen to know. Most people do not, and the attorney establishes it either way.",
};

export const LAND_SYSTEM_LABEL: Record<LandSystem, string> = {
  deed: "Deed registration — the deed chain is traced at the Land Registry",
  title: "Title registration (Bim Saviya)",
};

/**
 * What a notification channel actually is — said before it is agreed to, and
 * said again, from this same table, when it is withdrawn.
 *
 * WhatsApp gets the blunt version because it deserves it: it is a transfer of
 * the client's number and our messages to Meta, outside Sri Lanka (COMPLIANCE
 * §4). A client cannot consent to a cross-border transfer they were not told
 * about, and "notification preferences" is not telling them.
 *
 * `withdrawn` is the third field rather than a second table because the two
 * directions have to agree. A withdrawal screen that restates in its own words
 * what the intake screen promised is how the two drift, and the drift lands on
 * the sentence about Meta. Note what `withdrawn` carefully does NOT say: it
 * describes what the platform RECORDS, not a change in what it sends. Nothing
 * in the API sends on either channel today (no code path calls
 * `hasConsentForCase`), so promising that messages will stop would be claiming
 * a cause we have not established.
 *
 * `meaning` IS IN THE PERMISSIVE MOOD, AND THAT IS THE WHOLE POINT OF ITS
 * WORDING. It used to read "We email you to say something has happened" — the
 * present indicative, which asserts a practice. There is no practice: there is
 * no mailer and no WhatsApp client anywhere in apps/api/src, no dependency for
 * either, and `hasConsentForCase` is called by nothing but its own test. So a
 * client ticking the box was being told we do something we have never once
 * done. Every `meaning` therefore says what ticking PERMITS — "ticking this
 * lets us", "would be", "nothing more than" — and describes the LIMITS of the
 * permission, which are the part that stays true whenever the channel is
 * eventually built. The single statement that nothing is sent on either channel
 * yet is made once, at the fieldset that renders these (ClientCases) and again
 * on /data, rather than repeated into both rows here where it would bury the
 * limits under a disclaimer.
 *
 * WHY THE PERMISSION IS WORTH TAKING BEFORE THE CHANNEL EXISTS: consent under
 * PDPA is prior and specific. Recording it now, per case, per channel, is what
 * makes the first message lawful when there is one. Nothing about that requires
 * pretending the message already goes out.
 */
export const CHANNEL_CONSENT: Record<
  (typeof OPTIONAL_NOTIFICATION_CHANNELS)[number],
  { label: string; meaning: string; withdrawn: string }
> = {
  email: {
    label: "You may email me about this request",
    meaning:
      "Ticking this lets us use your email address to say something has happened on this case — " +
      "nothing more than that there is an update. Your documents would stay in this app and " +
      "would never be attached to an email.",
    withdrawn:
      "We record that you have withdrawn permission to use your email address for this. Your " +
      "email address is not deleted by this — it is how you sign in — and the record that you " +
      "gave permission earlier is kept rather than erased.",
  },
  whatsapp: {
    label: "You may message me on WhatsApp about this request",
    meaning:
      "Ticking this permits your phone number and our messages to travel through Meta's servers, " +
      "outside Sri Lanka, under their rules rather than ours. Nothing about your land would go " +
      "in a message, but the fact that you have a case with us would. That transfer is why this " +
      "is a separate, deliberate tick rather than a default — leave it unticked if you would " +
      "rather not. Note too that this application has nowhere to give us a telephone number and " +
      "holds none for you, so ticking it grants a permission before there is a number to use it " +
      "on.",
    withdrawn:
      "We record that you have withdrawn permission to send your number and our messages " +
      "through Meta's servers outside Sri Lanka. What Meta already holds from any earlier " +
      "message is on their systems under their rules, and withdrawing here does not reach it.",
  },
};
